import { imageControls, type ImageControls } from "./render/image-controls";
import { makePanelHeader } from "./util/panel";

interface ControlDef {
  key: keyof ImageControls;
  label: string;
  min: number;
  max: number;
  step: number;
}

const DEFS: ControlDef[] = [
  { key: "exposure",         label: "EXPOSURE", min: 0,   max: 4,   step: 0.01 },
  { key: "reinhardWhite",    label: "WHITE PT", min: 0.5, max: 32,  step: 0.1  },
  { key: "sigmoidMidpoint",  label: "MID",      min: 0,   max: 1,   step: 0.01 },
  { key: "sigmoidSteepness", label: "STEEP",    min: 0,   max: 20,  step: 0.1  },
  { key: "bloomStrength",    label: "BLOOM",    min: 0,   max: 1,   step: 0.01 },
  { key: "bloomThreshold",   label: "THRESH",   min: 0,   max: 10,  step: 0.05 },
  { key: "bloomScatter",     label: "SCATTER",  min: 0,   max: 2,   step: 0.01 },
  { key: "motionBlurAmount", label: "M-BLUR",   min: 0,   max: 1,   step: 0.01 },
];

const DEFAULTS: Record<keyof ImageControls, number> = {
  exposure:         imageControls.exposure,
  reinhardWhite:    imageControls.reinhardWhite,
  sigmoidMidpoint:  imageControls.sigmoidMidpoint,
  sigmoidSteepness: imageControls.sigmoidSteepness,
  bloomStrength:    imageControls.bloomStrength,
  bloomThreshold:   imageControls.bloomThreshold,
  bloomScatter:     imageControls.bloomScatter,
  motionBlurAmount: imageControls.motionBlurAmount,
};

export class ImageControlsPanel {
  private root: HTMLDivElement;
  private rows = new Map<keyof ImageControls, { slider: HTMLInputElement; num: HTMLInputElement }>();
  private toast: HTMLDivElement | null = null;
  private toastTimer: number | null = null;
  private visible = false;
  private hudBtn!: HTMLButtonElement;
  private hudVisible = false;

  constructor() {
    this.root = document.createElement("div");
    this.root.id = "image-controls-panel";
    this.root.className = "panel image-controls-panel";
    this.root.style.display = "none";

    this.root.appendChild(makePanelHeader("IMAGE", () => this.hide()));

    const body = document.createElement("div");
    body.className = "image-controls-body";
    for (const def of DEFS) body.appendChild(this.buildRow(def));
    this.root.appendChild(body);

    const footer = document.createElement("div");
    footer.className = "image-controls-footer";
    const mkBtn = (label: string, onClick: () => void): HTMLButtonElement => {
      const b = document.createElement("button");
      b.className = "ic-btn";
      b.textContent = label;
      b.addEventListener("click", onClick);
      footer.appendChild(b);
      return b;
    };
    this.hudBtn = mkBtn("", () => this.toggleHud());
    mkBtn("RESET", () => this.resetAll());
    mkBtn("COPY", () => this.copyAsCode());
    this.root.appendChild(footer);

    document.body.appendChild(this.root);

    this.applyHudVisibility();
  }

  private applyHudVisibility(): void {
    const hud = document.getElementById("hud");
    if (hud) hud.style.display = this.hudVisible ? "" : "none";
    this.hudBtn.textContent = this.hudVisible ? "HUD ON" : "HUD OFF";
    this.hudBtn.classList.toggle("active", this.hudVisible);
  }

  private toggleHud(): void {
    this.hudVisible = !this.hudVisible;
    this.applyHudVisibility();
  }

  private buildRow(def: ControlDef): HTMLDivElement {
    const row = document.createElement("div");
    row.className = "ic-row";

    const label = document.createElement("div");
    label.className = "ic-label";
    label.textContent = def.label;
    row.appendChild(label);

    const v0 = String(imageControls[def.key]);
    const mkInput = (type: "range" | "number", cls: string): HTMLInputElement => {
      const el = document.createElement("input");
      el.type = type;
      el.className = cls;
      el.min = String(def.min);
      el.max = String(def.max);
      el.step = String(def.step);
      el.value = v0;
      return el;
    };
    const slider = mkInput("range", "ic-slider");
    const num = mkInput("number", "ic-num");
    const syncFrom = (src: HTMLInputElement, dst: HTMLInputElement) => {
      const v = parseFloat(src.value);
      if (!Number.isFinite(v)) return;
      imageControls[def.key] = v;
      dst.value = src.value;
    };
    slider.addEventListener("input", () => syncFrom(slider, num));
    num.addEventListener("input", () => syncFrom(num, slider));

    row.appendChild(slider);
    row.appendChild(num);
    this.rows.set(def.key, { slider, num });
    return row;
  }

  private resetAll(): void {
    for (const def of DEFS) {
      const v = DEFAULTS[def.key];
      imageControls[def.key] = v;
      const r = this.rows.get(def.key);
      if (r) {
        r.slider.value = String(v);
        r.num.value = String(v);
      }
    }
    this.flash("RESET");
  }

  private copyAsCode(): void {
    const lines = DEFS.map((d) => `  ${d.key}: ${imageControls[d.key]},`);
    const code = `export const imageControls: ImageControls = {\n${lines.join("\n")}\n};`;
    const cb = navigator.clipboard;
    if (cb && cb.writeText) {
      cb.writeText(code).then(
        () => this.flash("COPIED"),
        () => this.flash("COPY FAIL"),
      );
    } else {
      this.flash("NO CLIPBOARD");
    }
  }

  private flash(msg: string): void {
    if (!this.toast) {
      this.toast = document.createElement("div");
      this.toast.className = "ic-toast";
      this.root.appendChild(this.toast);
    }
    this.toast.textContent = msg;
    this.toast.style.opacity = "1";
    if (this.toastTimer !== null) window.clearTimeout(this.toastTimer);
    this.toastTimer = window.setTimeout(() => {
      if (this.toast) this.toast.style.opacity = "0";
      this.toastTimer = null;
    }, 900);
  }

  show(): void {
    this.visible = true;
    this.root.style.display = "flex";
    this.syncToControls();
  }

  hide(): void {
    this.visible = false;
    this.root.style.display = "none";
  }

  toggle(): void { this.visible ? this.hide() : this.show(); }
  isVisible(): boolean { return this.visible; }

  private syncToControls(): void {
    for (const def of DEFS) {
      const r = this.rows.get(def.key);
      if (!r) continue;
      const v = imageControls[def.key];
      r.slider.value = String(v);
      r.num.value = String(v);
    }
  }
}
